import { createPopper, Instance } from '@popperjs/core';
import { Component } from 'react';

import { IconHelper } from '../../../utils/icon-helper';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import Tooltip from '../tooltip/tooltip.component';
import style from './dropdown.shared.style.module.scss';

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * If a dropdown value object carries a 6-digit hex color in its `value` property
 * (e.g. convert-options DROPDOWN_OPTION color nodes), return that hex so a swatch
 * can be rendered next to the label. Any other value shape renders unchanged.
 */
function getSwatchColor(value: unknown): string | null {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const hex = (value as { value?: unknown }).value;
    return typeof hex === 'string' && HEX_COLOR_RE.test(hex) ? hex : null;
}


export interface DropdownProps<T> {
    children?: any;
    values: T[];
    valueFormatter: (value: T) => string;
    hasTooltip: (value: T) => boolean;
    getTooltip: (value: T) => string;
    selectedValueFormatter?: (value: T) => string;
    initialValue?: T | undefined;
    placeholder?: string;
    label?: string;
    /**
     * UXFU1-06: the visible name of the group this dropdown sits in, when the caller draws it itself instead of passing
     * `label` (a convert-options group header, a field label). Names the value button "<ariaLabel>: <value>" and the
     * listbox, so a screen reader hears "Image Quality Preset: High", not just "High". Not rendered.
     */
    ariaLabel?: string;
    btnClassNames?: string;
    btnGroupClassNames?: string;
    firstBtnStyles?: any;
    onSelect?: (selectedValue: T, forced?: boolean) => void
    canSelect?: (selectedValue: T) => Promise<boolean>
    shownFn?: (value: T) => boolean;
    emitInitialValueOnInit?: boolean;
    searchable?: boolean;
}

interface DropdownState<T> {
    values: T[];
    selectedValue?: T | undefined;
    shown?: boolean;
    prevInitialValue?: T | undefined;
    searchTerm: string;
    isPopperReady: boolean;
}

let dropdownSeq = 0;

type FocusTarget = 'selected' | 'first' | 'last';

export default class Dropdown<T> extends Component<DropdownProps<T>, DropdownState<T>> {
    wrapperRef: any;
    /** UXFU1-06: ids for the listbox and its options (aria-controls; option ids), unique per instance. */
    private readonly listboxId = `xc-dropdown-${++dropdownSeq}`;
    /** An option to focus once the menu is visible (a `visibility: hidden` element cannot take focus). */
    private pendingFocus: FocusTarget | null = null;
    btnClassNames = 'x-btn x-btn-gray x-btn-gray-outline';
    firstBtnStyles = {};

    // Popper refs
    referenceElement: HTMLElement | null = null;
    popperElement: HTMLElement | null = null;
    popperInstance: Instance | null = null;

    readonly state: Readonly<DropdownState<T>> = {
        selectedValue: this.props.initialValue,
        values: this.props.values,
        shown: false,
        prevInitialValue: this.props.initialValue, // track initial value
        searchTerm: '',
        isPopperReady: false
    };
    constructor(props: DropdownProps<T>) {
        super(props);
        this.firstBtnStyles = this.props.firstBtnStyles;
        this.handleClickOutside = this.handleClickOutside.bind(this);

        this.btnClassNames = this.props.btnClassNames ?? this.btnClassNames
    }

    componentDidMount() {
        if (this.props.emitInitialValueOnInit) {
            this.updateSelectedValue(this.props.initialValue);
        }
    }

    componentWillUnmount() {
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
    }

    setWrapperRef(node: any) {
        this.wrapperRef = node;
    }

    setReferenceRef(node: any) {
        this.referenceElement = node;
    }

    /**
     * QA4-01 (prod fixes 2026-10-02 follow-up): Escape with the menu open closes the menu and hands focus back to the
     * value button. `preventDefault` marks the key as handled, so a dialog around the dropdown (the generic Modal, which
     * leaves a handled Escape alone) stays open with the user's unsaved edits. A closed dropdown leaves Escape alone.
     *
     * UXFU1-06 (fu-r2): the rest of the listbox keyboard pattern the button's `aria-haspopup="listbox"` promises
     * (WCAG 2.1.1 -- a keyboard user could open the menu but not choose anything):
     *  - closed, on the value button: ArrowDown / ArrowUp open it on the selected (or last) option; Enter / Space open
     *    it through the button's own click, and focus then moves into the list (the search box when there is one);
     *  - open: ArrowDown / ArrowUp move between options (ArrowDown from the search box enters the list, ArrowUp from the
     *    first option goes back to it), Home / End jump; Enter or Space chooses the focused option, and Enter in the
     *    search box chooses the first match; Tab closes the menu (focus back on the value button);
     *  - an option chosen either way closes the menu and returns focus to the value button.
     */
    private onKeyDown = (e: any) => {
        if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
        const key = e.key;
        if (!this.state.shown) {
            if ((key === 'ArrowDown' || key === 'ArrowUp') && e.target === this.valueButton()) {
                e.preventDefault();
                this.pendingFocus = key === 'ArrowUp' ? 'last' : 'selected';
                this.toggleDropdown();
            }
            return;
        }
        if (key === 'Escape' || key === 'Esc') {
            e.preventDefault();
            this.toggleDropdown();
            this.valueButton()?.focus({ preventScroll: true });
            return;
        }
        const options = this.optionElements();
        const at = options.indexOf(document.activeElement as HTMLElement);
        const inSearch = e.target === this.searchInput();
        switch (key) {
            case 'ArrowDown':
                e.preventDefault();
                if (at < 0) this.focusOption(inSearch ? 'first' : 'selected');
                else options[Math.min(at + 1, options.length - 1)]?.focus();
                return;
            case 'ArrowUp':
                e.preventDefault();
                if (at < 0) this.focusOption(inSearch ? 'last' : 'selected');
                else if (at === 0 && this.searchInput()) this.searchInput().focus();
                else options[Math.max(at - 1, 0)]?.focus();
                return;
            case 'Home':
            case 'End':
                if (at < 0) return; // the search box keeps its own Home / End
                e.preventDefault();
                options[key === 'Home' ? 0 : options.length - 1]?.focus();
                return;
            case 'Enter':
            case ' ':
            case 'Spacebar': {
                if (inSearch && key !== 'Enter') return; // a space is part of the search
                const index = at >= 0 ? at : (inSearch ? 0 : -1);
                const value = this.shownValues()[index];
                if (index < 0 || value === undefined) return;
                e.preventDefault();
                void this.userTapedOnDropdownItem(value);
                return;
            }
            case 'Tab':
                // From inside the popup the focused element is about to unmount: close and land on the value button.
                // From the value button itself (a mouse-opened menu), close and let Tab move on as usual.
                if (at >= 0 || inSearch) {
                    e.preventDefault();
                    this.toggleDropdown();
                    this.valueButton()?.focus({ preventScroll: true });
                } else {
                    this.toggleDropdown();
                }
                return;
            default:
        }
    };

    private valueButton(): HTMLElement | null {
        return this.referenceElement?.querySelector('button[aria-haspopup]') as HTMLElement | null;
    }

    private searchInput(): HTMLInputElement | null {
        return this.popperElement?.querySelector('input') as HTMLInputElement | null;
    }

    private optionElements(): HTMLElement[] {
        return this.popperElement ? Array.from(this.popperElement.querySelectorAll('[role="option"]')) as HTMLElement[] : [];
    }

    /** Focus an option of the open menu now, and again once Popper has made it visible. */
    private focusOption(target: FocusTarget) {
        const options = this.optionElements();
        if (options.length === 0) return;
        const selected = options.findIndex(o => o.getAttribute('aria-selected') === 'true');
        const index = target === 'first' ? 0 : target === 'last' ? options.length - 1 : Math.max(selected, 0);
        options[index].focus();
        if (document.activeElement !== options[index]) this.pendingFocus = target;
    }

    /**
     * The group's click toggles the menu. A click with `detail === 0` is the value button's Enter / Space: put focus in
     * the list (the search box autofocuses itself), as a mouse user needs nothing moved.
     */
    private onTriggerClick = (e: any) => {
        const opening = !this.state.shown;
        if (opening && e?.detail === 0 && !this.props.searchable) this.pendingFocus = 'selected';
        this.toggleDropdown();
    };

    /** The options the open menu lists, in order: `shownFn`, then the search term. */
    private shownValues(): T[] {
        const term = (this.state.searchTerm || '').toLowerCase();
        return (this.state.values || [])
            .filter(val => this.valueFilter(val))
            .filter(val => !term || this.props.valueFormatter(val).toLowerCase().includes(term));
    }

    setPopperRef(node: any) {
        this.popperElement = node;
    }

    handleClickOutside(event: any) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.toggleDropdown();
        }
    }

    toggleDropdown() {
        if (!this.state.shown) {
            document.addEventListener('mousedown', this.handleClickOutside, { passive: true });
        } else {
            document.removeEventListener('mousedown', this.handleClickOutside);
        }

        const nextShown = !this.state.shown;

        this.setState({
            shown: nextShown,
            searchTerm: '', // Reset search when toggled
            isPopperReady: false // Reset popper ready state
        }, () => {
            if (nextShown) {
                this.createPopperInstance();
            } else {
                this.destroyPopperInstance();
            }
        });
    }

    createPopperInstance() {
        if (this.referenceElement && this.popperElement) {
            this.popperInstance = createPopper(this.referenceElement, this.popperElement, {
                placement: 'bottom-start',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 4],
                        },
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: 8,
                        },
                    },
                    {
                        name: 'flip',
                        options: {
                            padding: 8,
                        },
                    },
                    {
                        name: 'computeStyles',
                        options: {
                            gpuAcceleration: false, // Use top/left instead of transform to avoid conflict with CSS animation
                        },
                    }
                ],
                onFirstUpdate: () => {
                    this.setState({ isPopperReady: true }, () => {
                        // UXFU1-06: a keyboard open asked for an option; it can take focus now that it is visible.
                        const target = this.pendingFocus;
                        this.pendingFocus = null;
                        if (target && this.state.shown) this.focusOption(target);
                    });
                }
            });
        }
    }

    destroyPopperInstance() {
        if (this.popperInstance) {
            this.popperInstance.destroy();
            this.popperInstance = null;
        }
    }

    handleSearchChange(e: any) {
        this.setState({
            searchTerm: e.target.value
        });
    }

    clearSearch() {
        this.setState({
            searchTerm: ''
        });
    }

    async userTapedOnDropdownItem(selectedValue: T) {
        if (this.props.canSelect) {
            if (!await this.props.canSelect(selectedValue)) {
                return;
            }
        }
        this.updateSelectedValue(selectedValue);
        this.toggleDropdown();
        // UXFU1-06: the chosen option is about to unmount; focus would fall to <body>. The value button is where the
        // user's choice now shows.
        this.valueButton()?.focus({ preventScroll: true });
    }

    updateSelectedValue(selectedValue: T, forced = false) {
        this.setState({
            selectedValue: selectedValue,
        });
        if (this.props.onSelect) {
            this.props.onSelect(selectedValue, forced);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.valueFilter(this.state.selectedValue)) {
            this.updateSelectedValue(this.props.values.find((v) => this.valueFilter(v)), true);
        }

        if (this.state.shown && !prevState.shown) {
            // Scroll selected item into view
            setTimeout(() => {
                const activeEl = this.wrapperRef?.querySelector(`.${style.active}`);
                const containerEl = this.wrapperRef?.querySelector(`.${style['dropdown-content']}`);
                if (activeEl && containerEl) {
                    containerEl.scrollTop = activeEl.offsetTop - (containerEl.clientHeight / 2) + (activeEl.clientHeight / 2);
                }
            }, 50);
        }
    }

    static getDerivedStateFromProps(props, state) {
        const newState: any = {
            values: props.values
        };

        // Detect change in initialValue prop
        if (props.initialValue !== state.prevInitialValue) {
            newState.selectedValue = props.initialValue;
            newState.prevInitialValue = props.initialValue;
        }
        // If initialValue didn't change, check if current selection is still valid
        else if (!props.values.some(item => item === state.selectedValue)) {
            // Current selection is invalid (not in new values list), revert to initialValue or clear
            newState.selectedValue = props.initialValue;
        }

        return newState;
    }

    private valueFilter(value: T) {
        return !this.props.shownFn || this.props.shownFn(value);
    }

    private renderColorSwatch(value: T) {
        const color = getSwatchColor(value);
        if (!color) {
            return null;
        }
        return <span
            className={style['color-swatch']}
            style={{ backgroundColor: color }}
            aria-hidden="true"
        ></span>;
    }

    render() {
        const getSelectedValue = () => {
            if (this.props.selectedValueFormatter) {
                return this.props.selectedValueFormatter(this.state.selectedValue)
            }
            if (this.props.valueFormatter) {
                return this.props.valueFormatter(this.state.selectedValue)
            }
            return this.props.placeholder || 'Not Selected'
        }
        const groupLabel = this.props.label || this.props.ariaLabel || undefined;
        const selectedLabel = this.state.selectedValue !== undefined && this.state.selectedValue !== null
            ? this.props.valueFormatter(this.state.selectedValue) : undefined;
        const listboxId = this.listboxId;
        return (
            <div ref={(node) => this.setWrapperRef(node)}
                className={`${style.dropdown} ${this.props.btnGroupClassNames || ''}`}
                onKeyDown={this.onKeyDown}
            >
                {
                    <div key="dropdownTrigger" ref={(node) => this.setReferenceRef(node)} className={`x-btn-group ${style['dropdown-btn-group']}`} onClick={this.onTriggerClick}>
                        {
                            this.props.label && <button type="button" className={`${this.btnClassNames} ${style['label']}`} disabled>{this.props.label}
                            </button>
                        }
                        {/* QA4-09: the value button is the one Tab stop; the disabled label is not reachable, so it joins the name.
                            UXFU1-06: so does a group label the caller draws itself (`ariaLabel`). */}
                        <button type="button" className={`${this.btnClassNames} ${style['value']}`} style={this.firstBtnStyles}
                            aria-haspopup="listbox" aria-expanded={!!this.state.shown}
                            aria-controls={this.state.shown ? listboxId : undefined}
                            aria-label={groupLabel ? `${groupLabel}: ${getSelectedValue() ?? ''}` : undefined}>
                            {this.renderColorSwatch(this.state.selectedValue)}
                            {getSelectedValue()}
                        </button>
                        {/* The caret is decoration (the group's click opens the menu): no second, unnamed Tab stop. */}
                        <button type="button" className={`${this.btnClassNames} ${style['caret']}`} tabIndex={-1} aria-hidden="true">&#9660;</button>
                    </div>
                }
                {/* UXFU1-06: the popup is a container; the search box sits above a real listbox (an input is not an
                    option, and a listbox may hold only options). */}
                <div key="dropdownMenu" ref={(node) => this.setPopperRef(node)} className={`${style["dropdown-content"]} ${this.state.shown ? style.show : ''}`} style={{ visibility: this.state.isPopperReady ? 'visible' : 'hidden' }}>
                    {
                        this.state.shown && this.props.searchable && (
                            <div className={style['search-item']} onClick={(e) => e.stopPropagation()}>
                                <div className={style['search-wrapper']}>
                                    <input
                                        type="text"
                                        className={style['dropdown-search']}
                                        placeholder="Search..."
                                        aria-label={groupLabel ? `Search ${groupLabel}` : 'Search options'}
                                        aria-controls={listboxId}
                                        value={this.state.searchTerm}
                                        onInput={(e) => this.handleSearchChange(e)}
                                        autoFocus
                                    />
                                    {this.state.searchTerm && (
                                        <button
                                            type="button"
                                            className={style['search-clear-btn']}
                                            onClick={() => this.clearSearch()}
                                            title="Clear search"
                                            aria-label="Clear search"
                                        >
                                            &times;
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    }
                    {/* The id only while open (what aria-controls points at), so a closed dropdown renders the same markup
                        wherever it is (render-equality specs compare innerHTML). */}
                    <ul role="listbox" id={this.state.shown ? listboxId : undefined} aria-label={groupLabel}>
                    {
                        this.state.shown && this.shownValues()
                            .map((val, index) => {
                                const label = this.props.valueFormatter(val);
                                const selected = label === selectedLabel;
                                return (
                                    <li key={`${index}`} id={`${listboxId}-option-${index}`} role="option" aria-selected={selected}
                                        tabIndex={-1} className={`${selected ? style.active : ''}`}
                                        onClick={() => this.userTapedOnDropdownItem(val)}>{this.renderColorSwatch(val)}{label}
                                        <span className={style.tooltip}>
                                            {
                                                this.props.hasTooltip(val) &&
                                                <Tooltip text={this.props.getTooltip(val)}>
                                                    {
                                                        IconHelper.getIconElement(HtmlEntityTypes.INFO_WITH_CIRCLE)
                                                    }
                                                </Tooltip>
                                            }
                                        </span>
                                    </li>
                                )
                            })
                    }
                    </ul>
                </div>
            </div>
        );
    }
}